import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Flex, Image, Spinner, Stack, Table, Text, Button } from '@chakra-ui/react';
import { debounce } from 'lodash';
import { FiRefreshCw } from 'react-icons/fi';

import { fetchAPI } from '@/api/fetch';
import { apiAdminGetUsers, GetUsersResponse } from '@/api/services/admin.service';
import { SearchBox } from '@/components/ui/searchbox';
import {
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot
} from '@/components/ui/pagination';
import { getAgencyPlanLabel, getAgencyPlanColor } from '@/types/enums';

import emptyJobImage from '@/assets/image/empty-job-cover.svg';
import UserDetailsDialog from './UserDetailsDialog';

type TableColumn = {
  label: string;
  field: 'name' | 'email' | 'agency' | 'plan';
};

const TABLE_COLUMNS: TableColumn[] = [
  { label: 'Name', field: 'name' },
  { label: 'Email', field: 'email' },
  { label: 'Agency Name', field: 'agency' },
  { label: 'Plan', field: 'plan' }
];

const PAGE_SIZE = 10;

interface UserTableRowProps {
  account: Account;
  onRowClick: (account: Account) => void;
}

const UserTableRow = ({ account, onRowClick }: UserTableRowProps) => {
  const renderCellContent = (field: TableColumn['field']) => {
    switch (field) {
      case 'agency':
        return (
          <Text maxW="200px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {account.agency.name}
          </Text>
        );
      case 'plan':
        return (
          <Box
            m="8px"
            w="fit-content"
            textAlign="center"
            border="1px solid"
            borderColor={getAgencyPlanColor(account.agency.plan)}
            borderRadius="8px"
            p="4px 8px"
            color={getAgencyPlanColor(account.agency.plan)}
            fontSize="12px"
            fontWeight="bold"
          >
            {getAgencyPlanLabel(account.agency.plan)}
          </Box>
        );
      case 'name':
        return (
          <Text maxW="200px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {account.name}
          </Text>
        );
      default:
        return (
          <Text maxW="200px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {account.email}
          </Text>
        );
    }
  };

  return (
    <Table.Row key={account.account_id} _hover={{ bg: 'neutrals.100' }}>
      {TABLE_COLUMNS.map(({ field }, index) => (
        <Table.Cell
          key={field}
          fontSize="14px"
          p="0"
          px="12px"
          cursor="pointer"
          onClick={() => onRowClick(account)}
        >
          <Box pl={index === 0 ? '32px' : 0}>{renderCellContent(field)}</Box>
        </Table.Cell>
      ))}
    </Table.Row>
  );
};

export default function UserManagement() {
  const [pageLoading, setPageLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [fetchDataError, setFetchDataError] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<Account>();
  const [isUserDetailsDialogOpen, setIsUserDetailsDialogOpen] = useState(false);

  const loadUsers = useCallback(
    async (page: number, searchKeyword: string = keyword) => {
      setPageLoading(true);
      try {
        const response = await fetchAPI<GetUsersResponse>({
          request: () =>
            apiAdminGetUsers({
              page,
              limit: PAGE_SIZE,
              keyword: searchKeyword
            })
        });

        if (response.success) {
          const { accounts, current_page, total_page } = response.data;
          setAccounts(accounts);
          if (accounts.length > 0) {
            setSelectedAccount(accounts[0]);
          }
          setCurrentPage(current_page);
          setTotalPages(total_page);
          setFetchDataError(false);
        } else {
          console.error('Failed to fetch users:', response.message);
          setFetchDataError(true);
        }
      } catch (error) {
        console.error('Failed to fetch users:', error);
        setFetchDataError(true);
      } finally {
        setPageLoading(false);
      }
    },
    [keyword]
  );

  useEffect(() => {
    loadUsers(currentPage);
  }, [currentPage, loadUsers]);

  const searchUsers = useMemo(
    () =>
      debounce(async (searchKeyword: string) => {
        setListLoading(true);
        setFetchDataError(false);
        setKeyword(searchKeyword);
        setCurrentPage(1);
        await loadUsers(1, searchKeyword);
        setListLoading(false);
      }, 500),
    [loadUsers]
  );

  const handleRetry = useCallback(() => {
    loadUsers(currentPage);
  }, [currentPage, loadUsers]);

  const handleRefresh = useCallback(() => {
    loadUsers(currentPage);
  }, [currentPage, loadUsers]);

  const handlePageChange = useCallback((details: { page: number; pageSize: number }) => {
    setCurrentPage(details.page);
  }, []);

  const handleUpdateAccount = useCallback((account: Account) => {
    setAccounts((prevAccounts) =>
      prevAccounts.map((a) => (a.account_id === account.account_id ? account : a))
    );
  }, []);

  const handleRowClick = useCallback((account: Account) => {
    setSelectedAccount(account);
    setIsUserDetailsDialogOpen(true);
  }, []);

  const renderTableContent = () => {
    if (listLoading || pageLoading) {
      return (
        <Table.Row>
          <Table.Cell colSpan={TABLE_COLUMNS.length}>
            <Flex justifyContent="center" py="32px">
              <Spinner boxSize="32px" borderWidth="4px" color="primary.700" />
            </Flex>
          </Table.Cell>
        </Table.Row>
      );
    }

    return accounts.map((account) => (
      <UserTableRow key={account.account_id} account={account} onRowClick={handleRowClick} />
    ));
  };

  const renderEmptyState = () => (
    <Flex flexDir="column" alignItems="center" justifyContent="center">
      <Image src={emptyJobImage} h="280px" w="280px" mt="64px" alt="No users found" />
      <Text fontSize="20px" fontWeight="bold" color="neutrals.700">
        {keyword ? 'No Users Found' : 'No Users Yet'}
      </Text>
      <Text fontSize="16px" color="neutrals.500">
        {keyword ? 'Try adjusting your search criteria' : "Let's get started with your first user."}
      </Text>
    </Flex>
  );

  const renderErrorState = () => (
    <Flex direction="column" justify="center" align="center" height="100%">
      <Text fontSize="20px" fontWeight="bold" color="error.700" textAlign="center" mb="8px">
        Unable to load data.
      </Text>
      <Text fontSize="16px" color="error.700" textAlign="center" mb="16px">
        Please try again later or contact support for assistance.
      </Text>
      <Button
        onClick={handleRetry}
        variant="outline"
        borderColor="primary.900"
        color="primary.900"
        borderRadius="8px"
        fontSize="16px"
        p="12px 24px"
      >
        Retry
      </Button>
    </Flex>
  );

  return (
    <Flex flexDir="column" justifyContent="top" alignItems="left" h="100%" p="12px">
      <Flex justifyContent="space-between" alignItems="center" mb="24px">
        <SearchBox placeholder="Search by name" search={searchUsers} />
        <Button
          onClick={handleRefresh}
          loading={pageLoading}
          variant="ghost"
          aria-label="Refresh user list"
        >
          <FiRefreshCw style={{ marginRight: '8px' }} />
          Refresh
        </Button>
      </Flex>

      {fetchDataError ? (
        renderErrorState()
      ) : (
        <>
          <Box overflowX="auto">
            <Table.Root>
              <Table.Header>
                <Table.Row bg="neutrals.50" h="48px">
                  {TABLE_COLUMNS.map(({ label, field }) => (
                    <Table.ColumnHeader
                      key={field}
                      fontSize="14px"
                      fontWeight="bold"
                      borderColor="neutrals.300"
                      pl={field === 'name' ? '32px' : 0}
                      width="auto"
                      _hover={{ bg: 'neutrals.100' }}
                    >
                      <Flex alignItems="center" px="12px">
                        {label}
                      </Flex>
                    </Table.ColumnHeader>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>{renderTableContent()}</Table.Body>
            </Table.Root>
          </Box>

          {!listLoading && !pageLoading && accounts.length > 0 && (
            <Flex justifyContent="flex-end" alignItems="center" mt="16px">
              <PaginationRoot
                count={totalPages * PAGE_SIZE}
                pageSize={PAGE_SIZE}
                page={currentPage}
                onPageChange={handlePageChange}
              >
                <Stack wrap="wrap" direction="row">
                  <PaginationPrevTrigger />
                  <Text fontSize="14px" color="neutrals.700" alignSelf="center" mx="8px">
                    {currentPage} / {totalPages}
                  </Text>
                  <PaginationNextTrigger />
                </Stack>
              </PaginationRoot>
            </Flex>
          )}

          {!listLoading && !pageLoading && accounts.length === 0 && renderEmptyState()}
        </>
      )}

      {selectedAccount && (
        <UserDetailsDialog
          accountId={selectedAccount.account_id}
          open={isUserDetailsDialogOpen}
          onClose={() => setIsUserDetailsDialogOpen(false)}
          onUpdate={handleUpdateAccount}
        />
      )}
    </Flex>
  );
}
